import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Typography,
} from '@mui/material'
import { Field, useField } from 'formik'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Document, Page } from 'react-pdf'

import { getProtectedS3 } from '../../../hooks/useS3Storage'

interface DriverAgreementProps {
  language?: string
}

const DriverAgreement = ({ language = 'en' }: DriverAgreementProps) => {
  const { t } = useTranslation()
  // eslint-disable-next-line
  const [field, meta] = useField('agreementAccepted')
  const hasError = meta.touched && meta.error
  const [agreementFile, setAgreementFile] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [numPages, setNumPages] = useState<null | number>(null)
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined
  )
  const containerRef = useRef<HTMLDivElement | null>(null)

  const onDocumentLoadSuccess = useCallback((pdf) => {
    setNumPages(pdf.numPages)
    setLoading(false)
  }, [])

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth - 32)
    }
  }, [])

  const getDocumentKeyByLanguage = (lang: string): string => {
    switch (lang) {
      case 'de':
        return 'fahrervereinbarung.pdf'
      case 'en':
        return 'uride_driver_agreement_form.pdf'
      case 'es':
        return 'acuerdo_conductor.pdf'
      default:
        return 'uride_driver_agreement_form.pdf'
    }
  }

  const documentKey = getDocumentKeyByLanguage(language)

  const getAgreementFile = async (fileKey: string) => {
    if (!fileKey) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const data = await getProtectedS3(`profile/${fileKey}`)
      if (data && data.href) {
        const response = await fetch(data.href)
        if (response.ok) {
          const blob = await response.blob()
          const blobUrl = URL.createObjectURL(blob)
          setAgreementFile(blobUrl)
        }
      } else {
        console.error('Error fetching PDF')
      }
    } catch (err) {
      console.error('Error fetching driver agreement document:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAgreementFile(documentKey)
  }, [documentKey])

  const pageComponents = useMemo(() => {
    if (!numPages) return null

    return Array.from(new Array(numPages), (_, index) => (
      <Page
        key={`page_${index + 1}`}
        pageNumber={index + 1}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        width={containerWidth}
      />
    ))
  }, [numPages, containerWidth])

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={1}
        ref={containerRef}
        sx={{
          alignItems: 'center',
          border: hasError ? '1px solid red' : 'none',
          display: 'flex',
          flexDirection: 'column',
          height: 400,
          mb: 2,
          overflowY: 'scroll',
          p: 2,
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : (
          <Document
            error={
              <Typography color="error">{t('common.pdf_error')}</Typography>
            }
            file={agreementFile}
            loading={<CircularProgress />}
            onLoadSuccess={onDocumentLoadSuccess}
            options={{ cMapPacked: true, cMapUrl: 'cmaps/' }}
          >
            {pageComponents}
          </Document>
        )}
      </Paper>

      <FormControlLabel
        control={
          <Field as={Checkbox} color="primary" name="agreementAccepted" />
        }
        label={t('auth.register.agreement_checkbox_label')}
      />

      {hasError && (
        <Typography color="error" variant="caption">
          {meta.error}
        </Typography>
      )}
    </Box>
  )
}

export default DriverAgreement
